import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { AdminGuard } from "../components/admin/AdminGuard";
import { AdminNav } from "../components/admin/AdminNav";

const STATUS_STYLE = {
  paid: "border-emerald-300 bg-emerald-50 text-emerald-700",
  pending: "border-gold/40 bg-gold/15 text-gold-dark",
  expired: "border-stone-200 bg-stone-100 text-stone-500",
};

const COLS = ["Student name", "Email", "Phone", "Course", "Start date", "Payment status"];

function EnrollmentsTable({ rows }) {
  if (rows === null) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>;
  if (rows.length === 0)
    return <p className="rounded-3xl border border-dashed border-border bg-white py-14 text-center text-sm text-muted-foreground" data-testid="enrollments-empty">No enrollments yet.</p>;
  return (
    <div className="overflow-x-auto rounded-3xl border border-border bg-white">
      <table className="w-full text-left text-sm" data-testid="enrollments-table">
        <thead className="border-b border-border bg-secondary/50">
          <tr>{COLS.map((c) => <th key={c} className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-border last:border-0" data-testid={`enrollment-row-${r.id}`}>
              <td className="px-5 py-4 font-semibold text-forest-deep">{r.name}</td>
              <td className="px-5 py-4">{r.email}</td>
              <td className="px-5 py-4">{r.phone}</td>
              <td className="px-5 py-4">{r.course_title}</td>
              <td className="px-5 py-4 whitespace-nowrap">{r.start_date}</td>
              <td className="px-5 py-4">
                <span data-testid={`enrollment-status-${r.id}`}
                  className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${STATUS_STYLE[r.payment_status] || STATUS_STYLE.pending}`}>
                  {r.payment_status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EnrollmentsContent() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/enrollments").then((r) => setRows(r.data)).catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, []);
  return (
    <div className="min-h-screen bg-bone pt-[72px]" data-testid="admin-enrollments-page">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Admin — Registrations</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-forest-deep sm:text-4xl">Enrollments</h1>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="enrollments-count">
            {rows ? `${rows.length} registration${rows.length === 1 ? "" : "s"} · ${rows.filter((r) => r.payment_status === "paid").length} paid` : ""}
          </p>
          <AdminNav />
        </Reveal>
        <div className="mt-8"><EnrollmentsTable rows={rows} /></div>
      </div>
    </div>
  );
}

export default function AdminEnrollmentsPage() {
  return <AdminGuard><EnrollmentsContent /></AdminGuard>;
}
